import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import toast from "react-hot-toast";

import {
  ISurveyAnswerItem,
  ISurveyQuestion,
} from "../libs/interfaces/services";
import surveysService from "../services/surveys";
import { isValidAnswer } from "../helpers/isValidAnswer";
import { useContextSurveyQuiz } from "./useSurveyQuiz";

/**
 * Hook que encapsula toda a lógica de navegação entre questões/tópicos,
 * verificação de obrigatórias e submit de respostas.
 */
export function useQuestionNavigation(
  surveyTopic: ISurveyQuestion,
  indexQuiz: number
) {
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false);

  const params = useParams();
  const navigate = useNavigate();
  const { surveyID, surveyTopicID, hashID } = params;

  const {
    surveyQuizIndex,
    setSurveyQuizIndex,
    setShowSurveyQuiz,
    answerList,
    handleAttAnswerList,
    numberTopics,
    numTotalQuestions,
    setTotalSurveyAnswered,
  } = useContextSurveyQuiz();

  /**
   * Verifica se TODAS as questões obrigatórias do tópico foram respondidas.
   */
  const areAllRequiredQuestionsAnswered = (answers: ISurveyAnswerItem[]) => {
    if (!surveyTopic || !surveyTopic.questions) {
      return false;
    }

    for (const question of surveyTopic.questions) {
      if (
        !question.groupsQuestionItens ||
        question.groupsQuestionItens.length === 0
      ) {
        continue;
      }

      for (const subQuestion of question.groupsQuestionItens) {
        if (subQuestion.required) {
          const answer = answers.find(
            (ans) => ans.idSubQuestion === subQuestion.idSubQuestion
          );
          if (!answer || !isValidAnswer(answer)) {
            return false;
          }
        }
      }
    }

    return true;
  };

  /**
   * Encontra o índice da primeira questão obrigatória não respondida.
   */
  const findFirstUnansweredRequired = (
    topicAnswers: ISurveyAnswerItem[]
  ): number | null => {
    for (let i = 0; i < surveyTopic.questions.length; i++) {
      const question = surveyTopic.questions[i];
      if (
        !question.groupsQuestionItens ||
        question.groupsQuestionItens.length === 0
      ) {
        continue;
      }

      for (const subQuestion of question.groupsQuestionItens) {
        if (subQuestion.required) {
          const answer = topicAnswers.find(
            (ans) => ans.idSubQuestion === subQuestion.idSubQuestion
          );
          if (!answer || !isValidAnswer(answer)) {
            return i;
          }
        }
      }
    }
    return null;
  };

  /**
   * Navega para a próxima tela (questão ou tópico).
   */
  const nextScreen = async () => {
    try {
      await handleAttAnswerList(surveyID, hashID, surveyTopicID);

      const allAnswersResponse = await surveysService.listSurveyAnswer(
        surveyID || "",
        hashID || ""
      );

      const allAnswers = allAnswersResponse.object || [];
      const answeredIds = new Set<number>();
      for (const answer of allAnswers) {
        if (isValidAnswer(answer)) {
          answeredIds.add(answer.idQuestion);
        }
      }
      setTotalSurveyAnswered(answeredIds.size);

      const allTopicAnswers = allAnswers.filter(
        (answer: any) => answer.idTopic === Number(surveyTopicID)
      );

      const allRequiredAnswered =
        areAllRequiredQuestionsAnswered(allTopicAnswers);

      const currentIndex = numberTopics
        .map((idTopic) => idTopic?.idTopic)
        .indexOf(surveyTopicID ? Number(surveyTopicID) : 1);

      if (numTotalQuestions === surveyQuizIndex + 1) {
        if (allRequiredAnswered) {
          if (
            numberTopics.length > 1 &&
            currentIndex >= 0 &&
            currentIndex < numberTopics.length - 1
          ) {
            const nextTopicId = numberTopics[currentIndex + 1]?.idTopic;
            if (nextTopicId) {
              window.location.href = `/${hashID}/survey-topic/${surveyID}/${nextTopicId}`;
              return;
            }
          }
          navigate(`/${hashID}/survey-finish/${surveyID}`);
        } else {
          toast.error(
            "Por favor, responda todas as questões obrigatórias antes de continuar."
          );
          const firstUnansweredIndex =
            findFirstUnansweredRequired(allTopicAnswers);
          if (firstUnansweredIndex !== null) {
            setSurveyQuizIndex(firstUnansweredIndex);
          }
        }
      } else {
        setSurveyQuizIndex((old) => old + 1);
      }
    } catch (error) {
      console.error("Erro ao navegar para próxima tela:", error);
      toast.error("Erro ao avançar para próxima questão.");
    }
  };

  /**
   * Monta o array de respostas e envia para a API.
   */
  const submitAnswers = async (data: Record<string, any>) => {
    if (isSubmittingRef.current || loading) {
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    const responseArr: ISurveyAnswerItem[] = [];

    for (const key in data) {
      const typeQuest = surveyTopic.questions[
        indexQuiz
      ].groupsQuestionItens.find(
        (quest) => quest.idSubQuestion === Number(key)
      )?.questionType;

      const idAnswer =
        answerList.find((answer) => answer.idSubQuestion === Number(key))
          ?.idAnswer ?? 0;

      const baseResponse = {
        idAnswer,
        idQuestion: Number(
          surveyTopic.questions[indexQuiz].groupsQuestionItens[0].idQuestion
        ),
        idTopic: Number(surveyTopicID),
        idSurvey: Number(surveyID),
        idSubQuestion: Number(key),
      };

      switch (typeQuest) {
        case "Text":
          responseArr.push({
            ...baseResponse,
            answerInt: 0,
            answerArray: "",
            answerString: data[key],
          });
          break;

        case "Int":
          responseArr.push({
            ...baseResponse,
            answerInt: Number(data[key]),
            answerArray: "",
            answerString: null,
          });
          break;

        case "Date":
          responseArr.push({
            ...baseResponse,
            answerInt: 0,
            answerArray: "",
            answerString: null,
            answerDate: String(new Date(data[key]).toISOString()),
          });
          break;

        case "Scale":
        case "Radio":
          responseArr.push({
            ...baseResponse,
            answerInt: Number(data[key]),
            answerArray: "",
            answerString: null,
          });
          break;

        case "MultiSelect": {
          const filteredArr = (data[key] ?? [])
            .filter((item: any) => item && item !== "false")
            .map((item: any) => Number(item));
          responseArr.push({
            ...baseResponse,
            answerInt: 0,
            answerArray: JSON.stringify(filteredArr),
            answerString: null,
          });
          break;
        }

        default:
          continue;
      }
    }

    const response = await surveysService.SurveyAnswerList(
      responseArr,
      hashID ? hashID : ""
    );

    try {
      if (response.status === 200) {
        toast.success("Resposta salva com sucesso!");
        await nextScreen();
      } else {
        toast.error("Não foi possivel enviar sua resposta.");
      }
    } catch (error) {
      console.error("Erro ao processar resposta:", error);
      toast.error("Erro ao processar sua resposta.");
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const handleGoBack = (deactivateAssistant: () => void) => {
    deactivateAssistant();
    if (surveyQuizIndex === 0) {
      setShowSurveyQuiz(false);
    } else {
      setSurveyQuizIndex((old) => old - 1);
    }
  };

  return {
    loading,
    submitAnswers,
    handleGoBack,
    surveyID,
    surveyTopicID,
    hashID,
  };
}
